import { useRef } from 'react'
import { useAnalyticsExportJob } from '~/features/analytics/use-analytics-export-job'
import { useSavedQueryParams } from '~/features/saved-query'

import { LOOKUP_FIELD_DEFS } from '@hap/analytics'
import { Button, Tooltip, TooltipContent, TooltipTrigger, toast } from '@hap/components'

export function ExportInteractions(props: {
  columns: Array<keyof typeof LOOKUP_FIELD_DEFS>
}) {
  const { timespan, filters } = useSavedQueryParams()
  const toastInstance = useRef<string | number>('')
  const { startExport, isRunning, progress } = useAnalyticsExportJob({
    onComplete: (url) => {
      window.location.href = url
      toast.success('Download Complete!', {
        id: toastInstance.current,
        description: `Click the download button if the file was not automatically downloaded.`,
        action: (
          <Button
            variant="secondary"
            onClick={() => {
              window.location.href = url
            }}
          >
            Download
          </Button>
        ),
        duration: 10000,
      })
    },
    onFailed: () => {
      toast.error('Oh no!', {
        id: toastInstance.current,
        description: 'There was a problem generating your CSV. Please refresh and try again.',
        duration: 3000,
      })
    },
    onConnectionUnavailable: () => {
      toast.error('Unable to start export because the connection is not ready. Please try again.')
    },
    onConnectionClosed: () => {
      toast.dismiss(toastInstance.current)
      toast.error('Connection lost', {
        id: toastInstance.current,
        description: 'Unable to reconnect to the server. Please try again later.',
        duration: 3000,
      })
    },
  })

  const start = async () => {
    try {
      await startExport({
        timespan: {
          start: timespan.start.toISOString(),
          end: timespan.end.toISOString(),
          timezone: 'UTC',
        },
        filters,
        fields: props.columns,
      })
    } catch (error) {
      toast.error('Failed to start export. Please try again.')
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          disabled={isRunning}
          onClick={start}
          variant="outline"
          size={isRunning ? 'default' : 'icon'}
          icon="download-simple"
          className="shrink-0"
        >
          {isRunning ? `Generating CSV (${progress}% Complete)` : null}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Export as CSV</TooltipContent>
    </Tooltip>
  )
}
